import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import axios from 'axios';
import MenuIcon from '@mui/icons-material/Menu';
import StarIcon from '@mui/icons-material/Star';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { getIconComponent } from '../utils/navIcons';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { usePinnedItems } from '../contexts/PinnedItemsContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import FavoritesFlyout from './shared/FavoritesFlyout';
import MoreItemsFlyout from './shared/MoreItemsFlyout';

const DRAWER_WIDTH = 240;

interface NavbarProps {
  drawerOpen: boolean;
  onDrawerToggle: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ drawerOpen, onDrawerToggle }) => {
  const navigate = useNavigate();
  const { isAuthenticated, authReady, user, logout, isSchoolAdmin } = useAuth();
  const { favorites } = useFavorites();
  const { pinnedItems, temporaryItem } = usePinnedItems();
  const { session, endImpersonation, loading: impersonationLoading } = useImpersonation();

  // Determine if we should show learner view (either real student or impersonating)
  const isLearnerView = session.is_impersonating || user?.user_profile?.role === 'student';

  // Debug logging
  console.log('[Navbar] Rendering with pinnedItems:', pinnedItems);
  console.log('[Navbar] pinnedItems length:', pinnedItems?.length);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [aiHealthStatus, setAiHealthStatus] = useState<'checking' | 'healthy' | 'error'>('checking');
  const [aiHealthMessage, setAiHealthMessage] = useState<string>('Checking AI connection...');
  const [aiHealthDetails, setAiHealthDetails] = useState<any>(null);
  const [showHealthDialog, setShowHealthDialog] = useState(false);
  const [showFavoritesFlyout, setShowFavoritesFlyout] = useState(false);
  const [showMoreItemsFlyout, setShowMoreItemsFlyout] = useState(false);

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/login');
  };

  const handleSettings = () => {
    handleUserMenuClose();
    navigate('/settings');
  };

  const handleMyPreferences = () => {
    handleUserMenuClose();
    navigate('/me/settings');
  };

  // AI Health Check
  useEffect(() => {
    const checkAIHealth = async () => {
      try {
        const response = await axios.get('/api/ai-health/');
        if (response.data.status === 'healthy') {
          setAiHealthStatus('healthy');
          setAiHealthMessage('AI services are operational');
          setAiHealthDetails(response.data);
        } else {
          setAiHealthStatus('error');
          setAiHealthMessage(response.data.message || 'AI services unavailable');
          setAiHealthDetails(response.data);
        }
      } catch (error: any) {
        setAiHealthStatus('error');
        setAiHealthMessage(error.response?.data?.message || 'Unable to connect to AI services');
        setAiHealthDetails(error.response?.data);
      }
    };

    if (isAuthenticated && authReady) {
      checkAIHealth();
      const interval = setInterval(checkAIHealth, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, authReady]);

  // Don't render navbar if not authenticated
  if (!isAuthenticated || !authReady) {
    return null;
  }

  return (
    <>
      {/* Impersonation Banner */}
      {session.is_impersonating && session.learner && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            py: 1,
            px: 2,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            🎭 Viewing as Learner: {session.learner.name} ({session.learner.student_id})
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => endImpersonation()}
            disabled={impersonationLoading}
          >
            {impersonationLoading ? 'Ending...' : 'Exit Impersonation'}
          </Button>
        </Box>
      )}

      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          top: session.is_impersonating ? '48px' : 0, // Shift down if impersonating
        }}
      >
        <Toolbar disableGutters>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={onDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Caliper
          </Typography>

          {/* AI Health Status */}
          <Tooltip title={aiHealthMessage}>
            <IconButton
              size="small"
              onClick={() => setShowHealthDialog(true)}
              sx={{ mr: 2 }}
            >
              {aiHealthStatus === 'checking' && <HourglassEmptyIcon />}
              {aiHealthStatus === 'healthy' && <CheckCircleIcon sx={{ color: 'lightgreen' }} />}
              {aiHealthStatus === 'error' && <ErrorIcon sx={{ color: 'orange' }} />}
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          <Button
            color="inherit"
            onClick={handleUserMenuClick}
            startIcon={<AccountCircleIcon />}
          >
            {user?.first_name || user?.username}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
          >
            <MenuItem onClick={handleMyPreferences}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>My preferences</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleSettings}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant="persistent"
        open={drawerOpen}
        sx={{
          // Width animates 240 → 0 when the hamburger collapses the drawer,
          // so main content actually reflows into the freed space instead of
          // leaving a 240 px empty column.
          width: drawerOpen ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          transition: (theme) =>
            theme.transitions.create('width', { duration: 200 }),
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            // AppBar height = 64px; impersonation banner adds ~48px above it.
            top: session.is_impersonating ? '112px' : '64px',
          },
        }}
      >
        <List>
          {/* Dashboard - Always visible */}
          <ListItemButton onClick={() => handleNavigate('/dashboard')}>
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>

          {/* Curriculum Reference — top-level for learners only.
              Teachers reach it via More Items → Teacher Tools, or pin it
              for one-click access. Avoids duplication when a teacher has
              already pinned it (it would otherwise appear twice). */}
          {isLearnerView && (
            <ListItemButton onClick={() => handleNavigate('/curriculum-reference')}>
              <ListItemIcon>
                <MenuBookIcon />
              </ListItemIcon>
              <ListItemText primary="Curriculum Reference" />
            </ListItemButton>
          )}

          {/* Teacher/Admin Navigation - Hide for learners */}
          {!isLearnerView && (
            <>
              <Divider sx={{ my: 1 }} />

              {/* Favorites - Fixed at top */}
              <ListItemButton onClick={() => setShowFavoritesFlyout(true)}>
                <ListItemIcon>
                  <StarIcon sx={{ color: '#ffd700' }} />
                </ListItemIcon>
                <ListItemText primary="Favorites" />
                {favorites && favorites.length > 0 && (
                  <Chip label={favorites.length} size="small" color="primary" />
                )}
              </ListItemButton>

              <Divider sx={{ my: 1 }} />

              {/* Pinned Items - Dynamic */}
              {pinnedItems && pinnedItems.length > 0 && (
                <>
                  {pinnedItems.map((item) => (
                    <ListItemButton key={item.id} onClick={() => handleNavigate(item.route)}>
                      <ListItemIcon>
                        {getIconComponent(item.icon)}
                      </ListItemIcon>
                      <ListItemText primary={item.name} secondary={item.section} />
                    </ListItemButton>
                  ))}
                  <Divider sx={{ my: 1 }} />
                </>
              )}

              {/* More Items - Fixed */}
              <ListItemButton onClick={() => setShowMoreItemsFlyout(true)}>
                <ListItemIcon>
                  <MoreHorizIcon />
                </ListItemIcon>
                <ListItemText primary="More Items" />
              </ListItemButton>
            </>
          )}

          {/* Temporary Item - Conditional */}
          {temporaryItem && (
            <>
              <Divider sx={{ my: 1 }} />
              <ListItemButton
                onClick={() => handleNavigate(temporaryItem.route)}
                sx={{
                  bgcolor: 'action.hover',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                }}
              >
                <ListItemIcon>
                  {getIconComponent(temporaryItem.icon)}
                </ListItemIcon>
                <ListItemText
                  primary={temporaryItem.name}
                  secondary={`${temporaryItem.section} (Temporary)`}
                />
              </ListItemButton>
            </>
          )}
        </List>

        {/* School Admin Badge - Hide for learners */}
        {!isLearnerView && isSchoolAdmin && (
          <Box sx={{ p: 2, mt: 'auto' }}>
            <Chip label="School Admin" color="secondary" size="small" />
          </Box>
        )}
      </Drawer>

      {/* Favorites Flyout */}
      <FavoritesFlyout
        open={showFavoritesFlyout}
        onClose={() => setShowFavoritesFlyout(false)}
      />

      {/* More Items Flyout */}
      <MoreItemsFlyout
        open={showMoreItemsFlyout}
        onClose={() => setShowMoreItemsFlyout(false)}
      />

      {/* AI Health Dialog */}
      <Dialog open={showHealthDialog} onClose={() => setShowHealthDialog(false)}>
        <DialogTitle>AI Service Status</DialogTitle>
        <DialogContent>
          <Alert severity={aiHealthStatus === 'healthy' ? 'success' : 'error'} sx={{ mb: 2 }}>
            {aiHealthMessage}
          </Alert>
          {aiHealthDetails && (
            <Box>
              <Typography variant="body2">
                <strong>Status:</strong> {aiHealthDetails.status}
              </Typography>
              {aiHealthDetails.api_key_configured !== undefined && (
                <Typography variant="body2">
                  <strong>API Key Configured:</strong>{' '}
                  {aiHealthDetails.api_key_configured ? 'Yes' : 'No'}
                </Typography>
              )}
              {aiHealthDetails.model && (
                <Typography variant="body2">
                  <strong>Model:</strong> {aiHealthDetails.model}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHealthDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;
